<template>
  <div
    class="card card-height animate-fade-in"
    @dragover.prevent="handleDragOver"
    @dragenter.prevent="handleDragEnter"
    @dragleave.prevent="handleDragLeave"
    @drop.prevent="handleDrop"
  >
    <div
      class="card-body d-flex flex-column justify-content-center align-items-center px-3 px-md-5"
    >
      <img
        class="configuration-diagram mb-4"
        :src="require('@/assets/img/app/diagram-image-generation.jpg')"
        alt="画像生成しチャオ"
      />
      <div
        class="w-100 mt-2 gap-3 d-flex flex-column flex-md-row justify-content-center"
      >
        <div
          class="input-group shadow-lg input-text-wrapper input-wrapper-layout align-items-end"
          :class="{ dragging: isDragging }"
        >
          <div class="file-preview-area" v-if="attachedFiles.length > 0">
            <div class="file-list-wrap">
              <div
                class="file-item-wrap"
                v-for="(file, index) in attachedFiles"
                :key="index"
              >
                <div class="file-content">
                  <template v-if="isImageFile(file)">
                    <img
                      class="file-image-preview"
                      :src="getFilePreview(file)"
                      :alt="file.name"
                    />
                  </template>
                  <template v-else>
                    <i class="fas fa-paperclip me-2 text-secondary"></i>
                    <span class="file-name">{{ file.name }}</span>
                  </template>
                </div>
                <button
                  class="btn-remove"
                  @click="removeFile(index)"
                  title="削除"
                >
                  <i class="fas fa-times"></i>
                </button>
              </div>
            </div>
          </div>

          <!-- ファイル添付ボタン -->
          <label for="file-upload" style="cursor: pointer; margin-right: 8px">
            <i
              class="fas fa-paperclip"
              style="font-size: 18px; color: #555"
            ></i>
          </label>
          <input
            type="file"
            id="file-upload"
            ref="fileInput"
            multiple
            @change="handleInputFileChange"
            style="display: none"
            accept=".jpeg,.jpg,.png"
          />
          <textarea
            :class="[
              'form-control',
              { 'border-0': true, 'shadow-none': true, expanded: focused },
            ]"
            placeholder="作成したい画像のイメージを入力してね！"
            @focus="focused = true"
            @blur="focused = false"
            v-model="userInput"
            @keydown.enter.exact.prevent="submitMessage"
            @keydown.enter.ctrl="insertNewLine"
            @paste="handlePaste"
            maxlength="1000"
          ></textarea>
          <!-- 送信ボタン -->
          <div
            class="btn btn-icon-only btn-icon-send bg-dark text-white rounded-circle"
            @click="submitMessage"
          >
            <i class="far fa-comment-dots" aria-hidden="true"></i>
          </div>
        </div>
        <ChatSelectBox
          v-model="selectedSize"
          :options="sizeOptions"
          label="画像サイズ"
          labelWide="w-100"
        />
      </div>
    </div>
  </div>
</template>

<script>
import ChatSelectBox from '@/components/PageLayout/App/ChatSelectBox.vue'

export default {
  name: 'DefaultCard',
  components: { ChatSelectBox },
  emits: [
    'handle-dragover',
    'handle-drop',
    'input-message',
    'upload-files',
    'remove-file',
    'handle-selected-size',
  ],
  props: {
    attachedFiles: {
      type: Array,
      required: true,
    },
    size: {
      type: String,
      default: '',
    },
    options: {
      type: Array,
    },
  },
  data() {
    return {
      userInput: '',
      focused: false,
      isDragging: false,
      dragCounter: 0,
      selectedSize: this.size,
      sizeOptions: this.options,
    }
  },
  methods: {
    handleDragEnter() {
      this.dragCounter++
      this.isDragging = true
    },
    handleDragLeave() {
      this.dragCounter--
      if (this.dragCounter === 0) {
        this.isDragging = false
      }
    },
    handleDrop(event) {
      this.dragCounter = 0
      this.isDragging = false
      this.$emit('handle-drop', event)
      this.$refs.fileInput.value = ''
    },
    handleDragOver(event) {
      this.$emit('handle-dragover', event)
      event.preventDefault()
    },
    handleInputFileChange(event) {
      this.$emit('upload-files', event)
      this.$refs.fileInput.value = ''
    },
    handlePaste(event) {
      const items = event.clipboardData?.items
      if (!items) return

      const files = []

      for (const item of items) {
        if (item.type.startsWith('image/')) {
          event.preventDefault()

          const file = item.getAsFile()
          if (!file) continue

          const ext = file.type.split('/')[1] || 'png'
          const fileName = `screenshot-${Date.now()}.${ext}`

          const newFile = new File([file], fileName, {
            type: file.type,
          })

          files.push(newFile)
        }
      }

      if (files.length === 0) return

      // ここがポイント：input[type=file]風のイベントを作る
      const pseudoEvent = {
        target: {
          files: files,
        },
      }

      // 既存ロジックに流す
      this.handleInputFileChange(pseudoEvent)
    },
    removeFile(index) {
      this.$emit('remove-file', index)
    },
    isImageFile(file) {
      return file.type.startsWith('image/')
    },
    getFilePreview(file) {
      return URL.createObjectURL(file)
    },
    getFileExtension(file) {
      return file.name.split('.').pop().toUpperCase()
    },
    submitMessage() {
      const trimmed = this.userInput.trim()
      if (trimmed) {
        this.$emit('input-message', trimmed)
        this.userInput = ''
      }
    },
    insertNewLine(event) {
      const textarea = event.target
      const start = textarea.selectionStart
      const end = textarea.selectionEnd
      this.userInput =
        this.userInput.substring(0, start) +
        '\n' +
        this.userInput.substring(end)
      this.$nextTick(() => {
        textarea.selectionStart = textarea.selectionEnd = start + 1
      })
    },
  },
  watch: {
    selectedSize(newVal) {
      this.$emit('handle-selected-size', newVal)
    },
  },
}
</script>
